import type {
  DrillingMultiAnalysisStateParams,
  WellIndexType,
  WidgetStateParams,
  ParametersStateParams,
  DirectionalDrillingState,
  HeatmapWidgetStateParams,
  WellLogsManagerWidgetStateParams,
  DayDepthChartWidgetStateParams,
  WellCorrelationWidgetStateParams,
} from '@go-widgets/well-logs-widget';
import type { Layout, Layouts } from '@profgeosoft/go-react-grid-layout';
import type { AlertsManagerWidgetStateParams, VideoBroadcastStateParams } from '@repo/go-widgets';
import type { FiltersWidgetStateParams } from '@repo/go-widgets/filters';
import type { TrajectoryWidgetStateParams } from '@repo/go-widgets/trajectory';
import type { TEnrichedWell, TFilterType, TGroupingType, TSortingType } from '@repo/well-list';
import { WellListPanelStore } from '@repo/well-list';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import {
  DAY_DEPTH_AVAILABLE,
  DIRECTIONAL_DRILLING_AVAILABLE,
  OPERATIONAL_PARAMETERS_AVAILABLE,
  TRAJECTORY_AVAILABLE,
  VIDEOBROADCAST_AVAILABLE,
  WELL_ANALYSE_AVAILABLE,
  WELL_LOGS_MANAGER_AVAILABLE,
  WELL_CORRELATION_AVAILABLE,
} from 'src/api/consts';
import { requireService } from 'src/packages/di';
import { hasValue } from 'src/packages/shared/utils/common';
import { equalsDeep } from 'src/packages/shared/utils/equalsDeep';
import { isStringArray } from 'src/packages/shared/utils/is-string-array';
import type { WellStatusMessage } from 'src/packages/well-status-service/addWellStatusService';
import { AlertsManagerWidgetEntity } from 'src/pages/dashboard/features/alerts-manager-widget/AlertsManagerWidget.entity';
import { DirectionalDrillingWidgetEntity } from 'src/pages/dashboard/features/directional-drilling-widget/DirectionalDrillingWidget.entity';
import { DrillingMultiAnalysisWidgetEntity } from 'src/pages/dashboard/features/drilling-multi-analysis-widget/DrillingMultiAnalysisWidget.entity';
import { getFilterCoordinator } from 'src/pages/dashboard/features/filter-coordinator/FilterCoordinator';
import { isFilterStateEmpty } from 'src/pages/dashboard/features/filter-coordinator/filtersValuesToFilterState';
import type { TFilterState } from 'src/pages/dashboard/features/filter-coordinator/types';
import { FiltersWidgetEntity } from 'src/pages/dashboard/features/filters-widget/FiltersWidget.entity';
import { GroupManager } from 'src/pages/dashboard/features/group-select/GroupManager';
import { GroupSelectStore } from 'src/pages/dashboard/features/group-select/GroupSelect.store';
import { HeatmapWidgetEntity } from 'src/pages/dashboard/features/heatmap-widget/HeatmapWidget.entity';
import { ImageBroadcastWidgetEntity } from 'src/pages/dashboard/features/image-broadcast-widget/ImageBroadcastWidget.entity';
import { OperationalParametersWidgetEntity } from 'src/pages/dashboard/features/operational-parameters-widget/OperationalParametersWidget.entity';
import { TrajectoryWidgetEntity } from 'src/pages/dashboard/features/trajectory-widget/TrajectoryWidget.entity';
import { WellAnalyseWidgetEntity } from 'src/pages/dashboard/features/well-analyse-widget/WellAnalyseWidget.entity';
import { WellCorrelationWidgetEntity } from 'src/pages/dashboard/features/well-correlation-widget/WellCorrelationWidget.entity';
import { DayDepthChartWidgetEntity } from 'src/pages/dashboard/features/well-day-depth-widget/DayDepthChartWidget.entity';
import { WellLogsManagerWidgetEntity } from 'src/pages/dashboard/features/well-logs-manager-widget/WellLogsManagerWidget.entity';
import { WellLogsWidgetEntity } from 'src/pages/dashboard/features/well-logs-widget/WellLogsWidget.entity';
import { COLS, ROWS_COUNT } from 'src/pages/dashboard/features/workbench/consts';
import { type TBreakpointsKeys } from 'src/pages/dashboard/features/workbench/types';
import { getDefaultLayouts, getPositionX, getPositionY } from 'src/pages/dashboard/features/workbench/utils/utils';
import { getCurrentBreakpoint } from 'src/pages/dashboard/utils/getCurrentBreakpoint';
import { setWellIdInGroupById } from 'src/pages/dashboard/utils/setWellIdInGroup';
import { mapWidget, mapWidgets } from 'src/serializers/widgets/mapWidgets';

import { VideobroadcastWidgetEntity } from '../../pages/dashboard/features/videobroadcast-widget/VideobroadcastWidget.entity';
import { WellListPanelState, type TWellListState } from '../well-list-panel-state';
import type { WidgetEntity, WidgetPosition, WidgetSize } from '../widget/WidgetEntity';
import type { TSerializedTab, TSerializedWidget } from '../workspace/types';

type TabOptions = {
  name: string;
  id: string;
  groups: TGroup[];
  rawWidgetsData: TSerializedWidget[];
  templateId?: number | null;
  layouts?: Layouts;
  wellListPanelState?: TWellListState | null;
};

export type TCreateWidgetOptions = {
  state?: object;
  groupId?: string | null;
  size?: WidgetSize;
  position?: WidgetPosition;
  isGroupsDefaultOpened?: boolean;
};

export type TGroup = {
  id: string;
  color: string;
  contrastColor: string;
};

export type TWellListCombinedData = {
  panelState: WellListPanelState;
  store: WellListPanelStore;
};

// TODO: куда-нибудь выбросить отсюда
const transformWellListState = (rawState: { [Key in keyof TWellListState]: unknown }): TWellListState => {
  const state: TWellListState = {
    filterBy: ['all', 'favorites', 'active'].includes(rawState.filterBy)
      ? (rawState.filterBy as TFilterType)
      : (() => {
          if (typeof rawState.filterBy !== 'number' || rawState.filterBy > 2) {
            return undefined;
          }

          switch (rawState.filterBy) {
            case 0: {
              return 'favorites';
            }

            case 1: {
              return 'active';
            }

            case 2: {
              return 'all';
            }

            default: {
              return undefined;
            }
          }
        })(),
    groupBy: ['status', 'rig', 'brigade', 'location'].includes(rawState.groupBy)
      ? (rawState.groupBy as TGroupingType)
      : (() => {
          if (typeof rawState.groupBy !== 'number' || rawState.groupBy > 3) {
            return undefined;
          }

          switch (rawState.groupBy) {
            case 0: {
              return 'status';
            }

            case 1: {
              return 'rig';
            }

            case 2: {
              return 'brigade';
            }

            case 3: {
              return 'location';
            }

            default: {
              return undefined;
            }
          }
        })(),
    searchValue: typeof rawState.searchValue === 'string' ? rawState.searchValue : '',
    selectedWellId: typeof rawState.selectedWellId === 'number' ? rawState.selectedWellId : undefined,
    activeKeys: isStringArray(rawState.activeKeys) ? rawState.activeKeys : undefined,
    groupId: typeof rawState.groupId === 'string' ? rawState.groupId : undefined,
    sorting: ['nameAsc', 'nameDesc', 'createdAtAsc', 'createdAtDesc'].includes(rawState.sorting)
      ? (rawState.sorting as TSortingType)
      : (() => {
          if (typeof rawState.sorting !== 'number' || rawState.sorting > 3) {
            return undefined;
          }

          switch (rawState.sorting) {
            case 0: {
              return 'nameAsc';
            }

            case 1: {
              return 'nameDesc';
            }

            case 2: {
              return 'createdAtAsc';
            }

            case 3: {
              return 'createdAtDesc';
            }

            default: {
              return undefined;
            }
          }
        })(),
    isPinned: typeof rawState.isPinned === 'boolean' ? rawState.isPinned : false,
  };

  return state;
};

const transformDefaultWellListGroup = (
  name:
    | 'wellsGroupingStatus'
    | 'wellsGroupingDrillingRig'
    | 'wellsGroupingRigCrew'
    | 'wellsGroupingLocation'
    | undefined,
): TGroupingType | undefined => {
  if (!name) {
    return undefined;
  }

  switch (name) {
    case 'wellsGroupingDrillingRig': {
      return 'rig';
    }

    case 'wellsGroupingLocation': {
      return 'location';
    }

    case 'wellsGroupingRigCrew': {
      return 'brigade';
    }

    case 'wellsGroupingStatus': {
      return 'status';
    }
  }
};

const customWellListFilterFn = (filterState: TFilterState): ((well: TEnrichedWell) => boolean) => {
  const filteredFilterRules = filterState.filter(({ value }) => hasValue(value));

  return (well) => {
    for (const { name, value } of filteredFilterRules) {
      switch (name) {
        case 'status': {
          if (Array.isArray(value)) {
            if (!value.some((v) => well.status.toLowerCase().includes(v.toString().toLowerCase()))) {
              return false;
            }
          } else {
            if (!well.status.toLowerCase().includes(value.toString().toLowerCase())) {
              return false;
            }
          }

          break;
        }

        // TODO: временно отключены, т.к. непонятно, откуда тянуть эти свойства
        // case 'workStartDateFrom': {
        //   if (typeof value !== 'number') {
        //     console.error('wrong type of an workStartDateFrom filter value');
        //     break;
        //   }

        //   break;
        // }

        // case 'workStartDateTo': {
        //   if (typeof value !== 'number') {
        //     console.error('wrong type of an workStartDateTo filter value');
        //     break;
        //   }

        //   break;
        // }

        case 'organizationId': {
          if (Array.isArray(value)) {
            if (!value.includes(well.block?.organizationId)) {
              return false;
            }
          } else {
            if (value !== well.block?.organizationId) {
              return false;
            }
          }

          break;
        }

        case 'blockId': {
          if (Array.isArray(value)) {
            if (!value.includes(well.block?.id)) {
              return false;
            }
          } else {
            if (value !== well.block?.id) {
              return false;
            }
          }

          break;
        }

        case 'fieldId': {
          if (Array.isArray(value)) {
            if (!value.includes(well.field?.id)) {
              return false;
            }
          } else {
            if (value !== well.field?.id) {
              return false;
            }
          }

          break;
        }

        case 'padId': {
          if (Array.isArray(value)) {
            if (!value.includes(well.padId)) {
              return false;
            }
          } else {
            if (value !== well.padId) {
              return false;
            }
          }

          break;
        }

        case 'id': {
          if (Array.isArray(value)) {
            if (!value.includes(well.id)) {
              return false;
            }
          } else {
            if (value !== well.id) {
              return false;
            }
          }

          break;
        }

        case 'rigModelId': {
          if (Array.isArray(value)) {
            if (!value.includes(well.rigModel?.id)) {
              return false;
            }
          } else {
            if (value !== well.rigModel?.id) {
              return false;
            }
          }

          break;
        }

        case 'brigadeId': {
          if (Array.isArray(value)) {
            if (!value.includes(well.brigadeId)) {
              return false;
            }
          } else {
            if (value !== well.brigadeId) {
              return false;
            }
          }

          break;
        }
      }
    }

    return true;
  };
};

const MAX_WIDGETS_COUNT = 20;

export class TabEntity {
  readonly groupManager: GroupManager;
  readonly initialLayouts: Layouts;
  readonly wellListPanelStore: WellListPanelStore;
  readonly wellListPanelState: WellListPanelState;

  @observable.ref layouts: Layouts;
  @observable name: string;
  @observable offsetTop?: number;
  @observable width: number = 0;
  @observable workbenchViewportHeight: number = 0;
  @observable workbenchContainerHeight: number = 0;
  @observable id: string;
  @observable isFocused: boolean = false;
  @observable readonly widgets: WidgetEntity[];
  @observable shouldScrollToViewport: boolean = false;
  @observable currentBreakpoint: TBreakpointsKeys | null = null;
  @observable templateId: number | null = null;
  @observable groupSelectStore: GroupSelectStore;
  wellListPanelStoreDisposer: Function | null = null;

  constructor(
    { name, id, groups, layouts, rawWidgetsData, templateId, wellListPanelState }: TabOptions,
    private readonly notifications = requireService('notifications'),
    private readonly widgetChangesApplyer = requireService('widgetChangesApplyer'),
    private readonly favoritesService = requireService('favoritesWells'),
    private readonly wellsData = requireService('wellsData'),
    private readonly userPreferences = requireService('userPreference'),
    private readonly wellStatusService = requireService('wellStatusService'),
  ) {
    this.groupManager = new GroupManager(this, groups);

    this.name = name;
    this.id = id;
    this.templateId = templateId ?? null;
    this.wellListPanelState = new WellListPanelState(
      wellListPanelState ? transformWellListState(wellListPanelState) : null,
    );
    this.wellListPanelStore = new WellListPanelStore(this.wellsData, this.favoritesService, {
      locationNameConfigurator: this.userPreferences.preferences.locationNameConfigurator,
      defaultFilterType: this.wellListPanelState.filterBy ?? this.userPreferences.preferences.defaultWellListFilter,
      defaultGroupingType:
        this.wellListPanelState.groupBy ??
        transformDefaultWellListGroup(this.userPreferences.preferences.defaultWellListGroup),
      defaultSortingType: this.wellListPanelState.sorting,
      selectedWellId: this.wellListPanelState.selectedWellId,
      searchValue: this.wellListPanelState.searchValue,
      activeKeys: this.wellListPanelState.activeKeys,
    });
    this.layouts = layouts || getDefaultLayouts();
    this.initialLayouts = layouts || getDefaultLayouts();
    const widgets = mapWidgets(rawWidgetsData, { wellListPanelStore: this.wellListPanelStore });
    this.widgets = widgets ?? [];
    this.groupSelectStore = new GroupSelectStore(this.groupManager, this.widgets, this.wellListCombinedData);

    if (!this.wellListPanelState.groupId) {
      const group =
        this.groupSelectStore.groupManager.getNotEmptyGroupId() ?? this.groupSelectStore.groupManager.getFirstGroup();
      if (group) {
        this.wellListPanelState.updateWellListPanelState({ groupId: group });
        this.wellListPanelState.updateWellListPanelState({
          selectedWellId: this.groupSelectStore.groupManager.getWellIdInGroup(group) ?? undefined,
        });
      }
    }

    makeObservable(this);
  }

  private onWellStatusChange = (wellStatusMessage: WellStatusMessage): void => {
    if (wellStatusMessage.status === 'COMPLETED') {
      void this.wellsData.load({ force: true });
    } else {
      this.wellListPanelStore.updateWellStatus(wellStatusMessage.wellId, wellStatusMessage.status);
    }
  };

  initWellListPanel(): VoidFunction {
    void this.wellsData.load();
    this.wellStatusService.subscribe('/wellStatus', this.onWellStatusChange);

    const filterCoordinator = getFilterCoordinator(this.groupSelectStore);

    const filterStateChangeDisposer = reaction(
      () => ({
        groupId: this.wellListPanelState.groupId ?? null,
        state: filterCoordinator.getState(this.wellListPanelState.groupId ?? null),
      }),
      ({ state }) => {
        if (isFilterStateEmpty(state) || !state) {
          this.wellListPanelStore.setCustomFilterFn(null);
          return;
        }

        this.wellListPanelStore.setCustomFilterFn(customWellListFilterFn(state));
      },
      { name: 'wellListPanel filterCoordinatorState', fireImmediately: true },
    );

    const wellListWellIdRestoreByGroupDisposer = reaction(
      () => this.wellListPanelState?.groupId,
      (groupId) => {
        if (!hasValue(groupId)) {
          return;
        }

        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        const result = setWellIdInGroupById(widgets, this.wellListPanelStore.selectedWellId);

        if (result) {
          this.wellListPanelStore.setSelectedWellId(result, { reason: 'groupIdChange' });
        }
      },
    );

    const activeKeysInitializationDisposer = reaction(
      () => ({
        groupingType: this.wellListPanelStore.groupingType,
        activeKeys: this.wellListPanelState.activeKeys,
        storeActiveKeys: this.wellListPanelStore.activeKeys,
        groupKeys: this.wellListPanelStore.wellGroups.map((group) => group.id),
      }),
      ({ groupingType, activeKeys, storeActiveKeys, groupKeys }, prev) => {
        if (!groupKeys.length) {
          return;
        }

        const isGroupingChanged = prev?.groupingType !== undefined && prev.groupingType !== groupingType;

        if (isGroupingChanged) {
          if (!equalsDeep(storeActiveKeys, groupKeys)) {
            this.wellListPanelStore.setActiveKeys(groupKeys);
          }

          if (!equalsDeep(activeKeys, groupKeys)) {
            this.wellListPanelState.updateWellListPanelState({ activeKeys: groupKeys });
          }
          return;
        }

        if (activeKeys !== undefined) {
          if (!equalsDeep(storeActiveKeys, activeKeys)) {
            this.wellListPanelStore.setActiveKeys(activeKeys);
          }
          return;
        }

        this.wellListPanelStore.setActiveKeys(groupKeys);
        this.wellListPanelState.updateWellListPanelState({ activeKeys: groupKeys });
      },
      { name: 'wellListPanel activeKeysInitialization', fireImmediately: true },
    );

    return () => {
      this.wellStatusService.unsubscribe('/wellStatus', this.onWellStatusChange);
      filterStateChangeDisposer();
      wellListWellIdRestoreByGroupDisposer();
      activeKeysInitializationDisposer();
    };
  }

  @computed
  get widgetsCollection(): ReadonlyMap<string, WidgetEntity> {
    const collection = new Map<string, WidgetEntity>();
    this.widgets.forEach((widget) => collection.set(widget.uuid, widget));

    return collection;
  }

  @computed
  get wellListCombinedData(): TWellListCombinedData {
    return {
      panelState: this.wellListPanelState,
      store: this.wellListPanelStore,
    };
  }

  @computed
  get fullscreenWidget(): WidgetEntity | null {
    return this.widgets.find((widget) => widget.isFullscreen) ?? null;
  }

  @action.bound
  applyChanges(data: TSerializedTab): void {
    this.name = data.name;

    const newWidgets: WidgetEntity[] = [];

    this.widgets.length = 0;

    data.widgets.forEach((rawWidget, index) => {
      const targetWidget = this.widgets.find((_widget) => _widget.uuid === rawWidget.uuid);

      if (targetWidget) {
        try {
          this.widgetChangesApplyer.applyChanges(targetWidget, rawWidget, index);
          newWidgets.push(targetWidget);
        } catch {
          newWidgets.push(mapWidget(rawWidget, index, { wellListPanelStore: this.wellListPanelStore }));
        }
      } else {
        newWidgets.push(mapWidget(rawWidget, index, { wellListPanelStore: this.wellListPanelStore }));
      }
    });

    this.widgets.push(...newWidgets);

    if (data.layouts) {
      for (const breakpointKey of Object.keys(data.layouts)) {
        if (
          !this.layouts[breakpointKey]?.length ||
          !equalsDeep(this.layouts[breakpointKey], data.layouts[breakpointKey])
        ) {
          this.layouts[breakpointKey] = data.layouts[breakpointKey];
        }
      }
    }
  }

  @action.bound
  setCurrentBreakpoint(breakpoint: TBreakpointsKeys): void {
    this.currentBreakpoint = breakpoint;
  }

  @action.bound
  setOffsetTop(value: number): void {
    this.offsetTop = value;
  }

  @action.bound
  setWidth(width: number): void {
    this.width = width;
  }

  @action.bound
  setHeight(value: number): void {
    this.workbenchViewportHeight = value;
  }

  @action.bound
  setRealHeight(height: number): void {
    this.workbenchContainerHeight = height;
  }

  @action.bound
  changeFocus(value: boolean): void {
    this.isFocused = value;
  }

  @action.bound
  setName(name: string): void {
    this.name = name;
  }

  @action.bound
  setShouldScrollToViewport(value: boolean): void {
    this.shouldScrollToViewport = value;
  }

  @action.bound
  createWellLogsManager(): string | null {
    return this.createWidget('well-logs-manager-widget', {
      groupId: this.wellListPanelState?.groupId ?? null,
    });
  }

  private getIsLimitExceeded(): boolean {
    if (this.widgets.length >= MAX_WIDGETS_COUNT) {
      this.notifications.showErrorMessageT('errors:maxNumberOfTabs');

      return true;
    }

    return false;
  }

  @action.bound
  createWellsFilterWidget(
    options: TCreateWidgetOptions = {
      groupId: this.wellListPanelState?.groupId ?? null,
    },
  ): string | null {
    return this.createFiltersWidget(null, options);
  }

  @action.bound
  createWellLogsWidget(
    wellIndexType: WellIndexType,
    wellId: number | null,
    stateParams: Partial<WellLogsManagerWidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): void {
    if (this.getIsLimitExceeded()) return;

    const defaultWidth = 79;
    const defaultHeight = 66;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new WellLogsWidgetEntity(
      {
        wellId,
        wellIndexType,
        isEditMode: false,
        stateParams: stateParams ?? {},
        type: 'well-logs-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
  }

  @action.bound
  createHeatmapWidget(
    wellId: number | null,
    stateParams?: Partial<HeatmapWidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded()) return null;

    const defaultWidth = 80;
    const defaultHeight = 80;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new HeatmapWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'heatmap-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createDrillingMultiAnalysisWidget(
    wellId: number | null,
    stateParams?: Partial<DrillingMultiAnalysisStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded()) return null;

    const defaultWidth = 150;
    const defaultHeight = 80;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new DrillingMultiAnalysisWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'drilling-multi-analysis-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
      this.wellListPanelStore,
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createWellAnalyseWidget(
    wellId: number | null,
    stateParams?: Partial<WidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded() || !WELL_ANALYSE_AVAILABLE) return null;

    const defaultWidth = 79;
    const defaultHeight = 66;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new WellAnalyseWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'well-analyse-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createImageBroadcastWidget(
    wellId: number | null,
    stateParams?: Partial<WidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded() || !WELL_ANALYSE_AVAILABLE) return null;

    const defaultWidth = 110;
    const defaultHeight = 80;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new ImageBroadcastWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'image-broadcast-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createOperationalParametersWidget(
    wellIndexType: WellIndexType,
    wellId: number | null,
    enableSound: boolean,
    stateParams?: Partial<ParametersStateParams> | null,
    options?: TCreateWidgetOptions,
  ): void {
    if (this.getIsLimitExceeded() || !OPERATIONAL_PARAMETERS_AVAILABLE) return;

    const defaultWidth = 79;
    const defaultHeight = 66;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new OperationalParametersWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'well-operational-params-widget',
        wellIndexType,
        enableSound,
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
  }

  @action.bound
  createDirectionalDrillingWidget(
    wellId: number | null,
    stateParams?: Partial<DirectionalDrillingState> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded() || !DIRECTIONAL_DRILLING_AVAILABLE) return null;

    const defaultWidth = 79;
    const defaultHeight = 66;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new DirectionalDrillingWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'directional-drilling-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createVideobroadcastWidget(
    wellId: number | null,
    stateParams?: Partial<VideoBroadcastStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded() || !VIDEOBROADCAST_AVAILABLE) return null;

    const defaultWidth = 79;
    const defaultHeight = 66;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new VideobroadcastWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'videobroadcast-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createDayDepthChartWidget(
    wellId: number | null,
    stateParams?: Partial<DayDepthChartWidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded() || !DAY_DEPTH_AVAILABLE) return null;

    const defaultWidth = 69;
    const defaultHeight = 56;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new DayDepthChartWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'time-vs-depth-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createWellLogsManagerWidget(
    wellId: number | null,
    stateParams?: Partial<WellLogsManagerWidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded() || !WELL_LOGS_MANAGER_AVAILABLE) return null;

    const defaultWidth = 69;
    const defaultHeight = 56;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new WellLogsManagerWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'well-logs-manager-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createWellCorrelationWidget(
    wellId: number | null,
    stateParams?: Partial<WellCorrelationWidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded() || !WELL_CORRELATION_AVAILABLE) return null;

    const defaultWidth = 200;
    const defaultHeight = 200;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new WellCorrelationWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'well-correlation-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createAlertsManagerWidget(
    wellId: number | null,
    stateParams?: Partial<AlertsManagerWidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded()) return null;

    const defaultWidth = 200;
    const defaultHeight = 200;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new AlertsManagerWidgetEntity(
      {
        wellId,
        stateParams: stateParams ?? {},
        type: 'alerts-manager-widget',
        isEditMode: false,
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createTrajectoryWidget(
    wellId: number | null,
    stateParams?: Partial<TrajectoryWidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded()) return null;
    if (!TRAJECTORY_AVAILABLE) return null;

    // Initial: 80% width × 100% height (min stays 50% × 60%)
    const defaultWidth = Math.round(COLS * 0.8);
    const defaultHeight = ROWS_COUNT;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new TrajectoryWidgetEntity(
      {
        wellId,
        wellboreId: null,
        trajectoryId: null,
        planOrFact: 'fact',
        stateParams: stateParams ?? {},
        type: 'trajectory-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 100,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createFiltersWidget(
    stateParams?: Partial<FiltersWidgetStateParams> | null,
    options?: TCreateWidgetOptions,
  ): string | null {
    if (this.getIsLimitExceeded()) return null;

    const defaultWidth = 47;
    const defaultHeight = 90;
    const optionalXPosition = options?.position?.x;
    const optionsYPosition = options?.position?.y;

    const newWidget = new FiltersWidgetEntity(
      {
        stateParams: (stateParams ?? {}) as FiltersWidgetStateParams,
        type: 'filters-widget',
      },
      {
        groupId: options?.groupId || null,
        isGroupsDefaultOpened: options?.isGroupsDefaultOpened ?? false,
        uuid: uuidv4(),
        isPinned: false,
        isFullscreen: false,
        zIndex: 500,
        size: {
          w: options?.size?.w || defaultWidth,
          h: options?.size?.h || defaultHeight,
        },
        position: {
          x: hasValue(optionalXPosition)
            ? optionalXPosition
            : getPositionX(defaultWidth, getCurrentBreakpoint(this.width)),
          y: hasValue(optionsYPosition)
            ? optionsYPosition
            : getPositionY(defaultHeight, this.offsetTop, this.workbenchViewportHeight),
        },
      },
    );

    this.widgets.push(newWidget);
    return newWidget.uuid;
  }

  @action.bound
  createWidget(type: string, options?: TCreateWidgetOptions): string | null {
    if (this.getIsLimitExceeded()) return null;

    if (type === 'filter-control' || type === 'filters-widget') {
      return this.createFiltersWidget(null, options);
    }

    if (type === 'well-analyse-widget') {
      return this.createWellAnalyseWidget(null, null, options);
    }

    if (type === 'image-broadcast-widget') {
      return this.createImageBroadcastWidget(null, null, options);
    }

    if (type === 'directional-drilling-widget') {
      return this.createDirectionalDrillingWidget(null, null, options);
    }

    if (type === 'videobroadcast-widget') {
      return this.createVideobroadcastWidget(null, null, options);
    }

    if (type === 'time-vs-depth-widget') {
      return this.createDayDepthChartWidget(null, null, options);
    }

    if (type === 'well-logs-manager-widget') {
      return this.createWellLogsManagerWidget(null, null, options);
    }

    if (type === 'alerts-manager-widget') {
      return this.createAlertsManagerWidget(null, null, options);
    }

    if (type === 'well-correlation-widget') {
      return this.createWellCorrelationWidget(null, null, options);
    }

    if (type === 'trajectory-widget') {
      return this.createTrajectoryWidget(null, null, options);
    }

    if (type === 'drilling-multi-analysis-widget') {
      return this.createDrillingMultiAnalysisWidget(null, null, options);
    }

    return null;
  }

  @action.bound
  addWidget(widget: WidgetEntity): void {
    if (this.getIsLimitExceeded()) return;

    this.widgets.push(widget);
  }

  @action.bound
  deleteWidget(widget: WidgetEntity): void {
    this.destroyWidgetEntity(widget);
    this.widgets.remove(widget);
  }

  destroyWidgetEntity(widget: WidgetEntity): void {
    if (widget.destroy) {
      widget.destroy();
    }
  }

  destroyWidgetEntities() {
    this.widgets.forEach((widget) => {
      this.destroyWidgetEntity(widget);
    });
  }

  destroy() {
    this.destroyWidgetEntities();
    this.wellListPanelStoreDisposer?.();
  }

  @action.bound
  onLayoutChange(allLayouts: Layouts): void {
    this.layouts = allLayouts;
  }

  @action.bound
  getExistingWidgetLayout(uuid: string, breakpoint: TBreakpointsKeys): Layout | null {
    return this.layouts[breakpoint]?.find((widgetLayout) => widgetLayout.i === uuid) || null;
  }

  @computed
  get operationalParametersWidgetList() {
    return this.widgets.filter(isOperationalParametersWidget);
  }

  @computed
  get alarm() {
    return this.operationalParametersWidgetList.some((w) => w.hasAlarm);
  }
}

function isOperationalParametersWidget(widget: WidgetEntity): widget is OperationalParametersWidgetEntity {
  return widget.type === 'well-operational-params-widget';
}
